import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { api, ApiError } from '../api/client';
import './OAuthApps.css';

type RunStatus = 'queued' | 'running' | 'completed' | 'failed';
type ItemStatus = 'pending' | 'running' | 'added' | 'already' | 'error';

interface RunInfo {
  id: string;
  client_id: string;
  status: RunStatus;
  panels_total: number;
  panels_done: number;
  panels_errored: number;
  panels_already: number;
  auto_retry_enabled: boolean;
  auto_retry_attempts_used: number;
  auto_retry_attempts_cap: number;
  triggered_by: string | null;
  created_at: string;
  started_at: string | null;
  finished_at: string | null;
}

interface RunItem {
  panel_email: string;
  source: 'panel' | 'inventory_serken' | 'inventory_regular';
  status: ItemStatus;
  error_code: string | null;
  error_message: string | null;
}

interface CreateRunResp {
  run_id: string;
  items_enqueued: { panel_email: string; source: string }[];
  skipped: { email: string; reason: string }[];
  invalid: string[];
}

interface Toast {
  type: 'success' | 'error' | 'info';
  message: string;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const CLIENT_ID_RE = /^[A-Za-z0-9._-]+\.apps\.googleusercontent\.com$/;
const SOURCE_LABEL: Record<RunItem['source'], string> = {
  panel: 'Panel',
  inventory_serken: 'Serken',
  inventory_regular: 'Regular',
};

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function OAuthApps(): JSX.Element {
  const [clientId, setClientId] = useState('');
  const [emailsText, setEmailsText] = useState('');
  const [starting, setStarting] = useState(false);
  const [runs, setRuns] = useState<RunInfo[]>([]);
  const [openRunId, setOpenRunId] = useState<string | null>(null);
  const [detail, setDetail] = useState<{ run: RunInfo; items: RunItem[] } | null>(null);
  const [retrying, setRetrying] = useState(false);
  const [toast, setToast] = useState<Toast | null>(null);
  const detailPollRef = useRef<number | null>(null);

  const showToast = useCallback((type: Toast['type'], message: string) => {
    setToast({ type, message });
    window.setTimeout(() => setToast(null), 6000);
  }, []);

  const parsedCount = useMemo(() => {
    const tokens = emailsText.split(/[\s,;]+/).map((t) => t.trim()).filter(Boolean);
    const valid = new Set(
      tokens.map((t) => t.toLowerCase()).filter((t) => EMAIL_RE.test(t)),
    );
    const invalid = tokens.filter((t) => !EMAIL_RE.test(t.toLowerCase())).length;
    return { valid: valid.size, invalid };
  }, [emailsText]);

  const clientIdValid = CLIENT_ID_RE.test(clientId.trim());

  const refreshRuns = useCallback(async (): Promise<void> => {
    try {
      const r = await api<{ runs: RunInfo[] }>('/api/oauth-runs?limit=15');
      setRuns(r.runs);
    } catch {
      // transient — next poll retries
    }
  }, []);

  useEffect(() => {
    void refreshRuns();
  }, [refreshRuns]);

  // List poll: 3s while a run is in flight, else 7s.
  useEffect(() => {
    const hasInflight = runs.some((r) => r.status === 'queued' || r.status === 'running');
    const timer = window.setInterval(() => void refreshRuns(), hasInflight ? 3000 : 7000);
    return () => window.clearInterval(timer);
  }, [runs, refreshRuns]);

  const refreshDetail = useCallback(
    async (runId: string): Promise<void> => {
      try {
        const d = await api<{ run: RunInfo; items: RunItem[] }>(`/api/oauth-runs/${runId}`);
        setDetail(d);
      } catch (err) {
        showToast('error', err instanceof Error ? err.message : 'Failed to load run');
      }
    },
    [showToast],
  );

  // Detail poll: 2.5s until terminal.
  useEffect(() => {
    if (detailPollRef.current !== null) {
      window.clearInterval(detailPollRef.current);
      detailPollRef.current = null;
    }
    if (!openRunId) {
      setDetail(null);
      return;
    }
    void refreshDetail(openRunId);
    detailPollRef.current = window.setInterval(() => void refreshDetail(openRunId), 2500);
    return () => {
      if (detailPollRef.current !== null) window.clearInterval(detailPollRef.current);
    };
  }, [openRunId, refreshDetail]);

  useEffect(() => {
    if (
      detail &&
      (detail.run.status === 'completed' || detail.run.status === 'failed') &&
      detailPollRef.current !== null
    ) {
      window.clearInterval(detailPollRef.current);
      detailPollRef.current = null;
    }
  }, [detail]);

  async function startRun(): Promise<void> {
    setStarting(true);
    try {
      const r = await api<CreateRunResp>('/api/oauth-runs', {
        method: 'POST',
        body: JSON.stringify({ client_id: clientId.trim(), emails_text: emailsText }),
      });
      const notes: string[] = [];
      if (r.skipped.length > 0) {
        const byReason = new Map<string, number>();
        for (const s of r.skipped) byReason.set(s.reason, (byReason.get(s.reason) ?? 0) + 1);
        notes.push(
          `skipped ${r.skipped.length} (${[...byReason].map(([k, v]) => `${k}: ${v}`).join(', ')})`,
        );
      }
      if (r.invalid.length > 0) notes.push(`invalid tokens: ${r.invalid.length}`);
      if (r.run_id) {
        showToast(
          'success',
          `Run started — ${r.items_enqueued.length} panel(s)${notes.length ? ` · ${notes.join(' · ')}` : ''}`,
        );
        setEmailsText('');
        await refreshRuns();
        setOpenRunId(r.run_id);
      } else {
        showToast('error', `Nothing to enqueue — ${notes.join(' · ') || 'no emails matched'}`);
      }
    } catch (err) {
      const e = err as ApiError;
      const invalid = (e.data as { invalid?: string[] } | undefined)?.invalid;
      showToast(
        'error',
        `${e.message}${invalid?.length ? ` (invalid: ${invalid.slice(0, 5).join(', ')}${invalid.length > 5 ? '…' : ''})` : ''}`,
      );
    } finally {
      setStarting(false);
    }
  }

  async function retryErrors(runId: string): Promise<void> {
    setRetrying(true);
    try {
      const r = await api<{ retried: number }>(`/api/oauth-runs/${runId}/retry-errors`, {
        method: 'POST',
      });
      showToast(
        r.retried > 0 ? 'success' : 'info',
        r.retried > 0 ? `Re-enqueued ${r.retried} item(s)` : 'No errored items to retry',
      );
      await refreshDetail(runId);
    } catch (err) {
      showToast('error', err instanceof Error ? err.message : 'Retry failed');
    } finally {
      setRetrying(false);
    }
  }

  async function toggleAutoRetry(runId: string, enabled: boolean): Promise<void> {
    try {
      await api(`/api/oauth-runs/${runId}/auto-retry`, {
        method: 'POST',
        body: JSON.stringify({ enabled }),
      });
      await refreshDetail(runId);
    } catch (err) {
      showToast('error', err instanceof Error ? err.message : 'Toggle failed');
    }
  }

  if (openRunId && detail) {
    const { run, items } = detail;
    const terminal = run.status === 'completed' || run.status === 'failed';
    return (
      <div className="oa-root">
        <div className="oa-scroll">
          <div className="oa-runheader">
            <button
              type="button"
              className="oa-btn oa-btn--ghost oa-btn--small"
              onClick={() => setOpenRunId(null)}
            >
              ← Back
            </button>
            <h1 className="oa-title">OAuth run</h1>
            <span className={`oa-status oa-status--${run.status}`}>{run.status}</span>
          </div>

          {toast && <div className={`oa-toast oa-toast--${toast.type}`}>{toast.message}</div>}

          <div className="oa-runmeta">
            <div>
              <strong>Client ID</strong>
              <code className="oa-mono">{run.client_id}</code>
            </div>
            <div>
              <strong>Created</strong>
              {fmtDate(run.created_at)}
            </div>
            <div>
              <strong>Triggered by</strong>
              {run.triggered_by ?? '—'}
            </div>
          </div>

          <div className="oa-runsummary">
            <span className="oa-summary-pill">{run.panels_total} total</span>
            <span className="oa-summary-pill oa-summary-pill--ok">
              {run.panels_done - run.panels_already} added
            </span>
            <span className="oa-summary-pill oa-summary-pill--already">
              {run.panels_already} already
            </span>
            <span className="oa-summary-pill oa-summary-pill--err">
              {run.panels_errored} errored
            </span>
          </div>

          <div className="oa-runactions">
            <label className="oa-filter">
              <input
                type="checkbox"
                checked={run.auto_retry_enabled}
                onChange={(e) => void toggleAutoRetry(run.id, e.target.checked)}
              />
              Auto-retry errored ({run.auto_retry_attempts_used}/{run.auto_retry_attempts_cap} used)
            </label>
            <div className="oa-actions-spacer" />
            <button
              type="button"
              className="oa-btn oa-btn--primary oa-btn--small"
              onClick={() => void retryErrors(run.id)}
              disabled={retrying || !terminal || run.panels_errored === 0}
              title={terminal ? 'Reset errored items and re-enqueue them' : 'Available once the run finishes'}
            >
              {retrying ? 'Retrying…' : `Retry errors (${run.panels_errored})`}
            </button>
          </div>

          <section className="oa-card">
            <div className="oa-card-header">
              <h2 className="oa-card-title">Panels</h2>
            </div>
            <div className="oa-table-wrap">
              <table className="oa-table">
                <thead>
                  <tr>
                    <th>Email</th>
                    <th>Source</th>
                    <th className="oa-col-center">Status</th>
                    <th>Error</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((it) => (
                    <tr key={it.panel_email}>
                      <td className="oa-cell-mono">{it.panel_email}</td>
                      <td>{SOURCE_LABEL[it.source]}</td>
                      <td className="oa-col-center">
                        <span className={`oa-status oa-status--${it.status}`}>{it.status}</span>
                      </td>
                      <td className="oa-cell-error">
                        {it.error_message ? `${it.error_code ?? ''} ${it.error_message}` : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </div>
      </div>
    );
  }

  return (
    <div className="oa-root">
      <div className="oa-scroll">
        <header className="oa-header">
          <h1 className="oa-title">OAuth Apps</h1>
          <p className="oa-subtitle">
            Trust an OAuth client ID across admin panels. Paste panel emails (any of: one per
            line, commas, spaces) — each panel is matched against the licensing panels and the
            Serken/Regular inventory, then a worker logs in and adds the app as{' '}
            <strong>Trusted</strong> under API Controls. Panels that already trust the app are
            skipped automatically.
          </p>
        </header>

        {toast && <div className={`oa-toast oa-toast--${toast.type}`}>{toast.message}</div>}

        <section className="oa-card">
          <div className="oa-card-header">
            <h2 className="oa-card-title">Start a run</h2>
          </div>
          <div className="oa-card-body">
            <label className="oa-field">
              <span className="oa-field-label">OAuth client ID</span>
              <input
                type="text"
                className="oa-input oa-mono"
                placeholder="123456789-abc123.apps.googleusercontent.com"
                value={clientId}
                onChange={(e) => setClientId(e.target.value)}
                spellCheck={false}
              />
              {clientId.trim() !== '' && !clientIdValid && (
                <span className="oa-field-error">Must end in .apps.googleusercontent.com</span>
              )}
            </label>
            <label className="oa-field">
              <span className="oa-field-label">
                Panel emails
                {parsedCount.valid > 0 && ` — ${parsedCount.valid} parsed`}
                {parsedCount.invalid > 0 && ` (${parsedCount.invalid} invalid ignored)`}
              </span>
              <textarea
                className="oa-textarea oa-mono"
                rows={8}
                placeholder={'admin@domain1.com\nadmin@domain2.com'}
                value={emailsText}
                onChange={(e) => setEmailsText(e.target.value)}
                spellCheck={false}
              />
            </label>
            <div className="oa-actions-row">
              <div className="oa-actions-spacer" />
              <button
                type="button"
                className="oa-btn oa-btn--primary"
                onClick={() => void startRun()}
                disabled={starting || !clientIdValid || parsedCount.valid === 0}
              >
                {starting
                  ? 'Starting…'
                  : `Start run (${parsedCount.valid} panel${parsedCount.valid === 1 ? '' : 's'})`}
              </button>
            </div>
          </div>
        </section>

        <section className="oa-card">
          <div className="oa-card-header">
            <h2 className="oa-card-title">Recent runs</h2>
          </div>
          <div className="oa-table-wrap">
            <table className="oa-table">
              <thead>
                <tr>
                  <th>Created</th>
                  <th>Client ID</th>
                  <th className="oa-col-center">Status</th>
                  <th className="oa-col-num">Done</th>
                  <th className="oa-col-num">Already</th>
                  <th className="oa-col-num">Errored</th>
                  <th className="oa-col-num">Total</th>
                  <th>By</th>
                </tr>
              </thead>
              <tbody>
                {runs.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="oa-empty">
                      No runs yet.
                    </td>
                  </tr>
                ) : (
                  runs.map((r) => (
                    <tr key={r.id} className="oa-row--clickable" onClick={() => setOpenRunId(r.id)}>
                      <td className="oa-cell-mono">{fmtDate(r.created_at)}</td>
                      <td className="oa-cell-mono oa-cell-clip" title={r.client_id}>
                        {r.client_id}
                      </td>
                      <td className="oa-col-center">
                        <span className={`oa-status oa-status--${r.status}`}>{r.status}</span>
                      </td>
                      <td className="oa-col-num">{r.panels_done}</td>
                      <td className="oa-col-num">{r.panels_already}</td>
                      <td className="oa-col-num">{r.panels_errored}</td>
                      <td className="oa-col-num">{r.panels_total}</td>
                      <td className="oa-cell-faint">{r.triggered_by ?? '—'}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </div>
  );
}
